import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash, PencilSimple, FloppyDisk, UploadSimple, Star, FilePdf, X } from '@phosphor-icons/react';
import { API, BACKEND_URL } from './adminConstants';
import { ShareLinkedInButton } from './ShareLinkedInButton';

export function CaseStudiesManagement() {
  const { getAuthHeader } = useAuth();
  const [stories, setStories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editStory, setEditStory] = useState(null);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', date: '', author: '', client: '', sector: '', image_url: '', slug: '', featured: false, pdf_url: '', pdf_filename: '' });
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const load = async () => { try { const r = await axios.get(`${API}/case-studies`); setStories(r.data); } catch { toast.error('Failed to load'); } };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ title: '', excerpt: '', content: '', date: '', author: '', client: '', sector: '', image_url: '', slug: '', featured: false, pdf_url: '', pdf_filename: '' }); setEditStory(null); setShowForm(false); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/news/upload-image`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false); e.target.value = '';
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file'); e.target.value = ''; return;
    }
    setUploadingPdf(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/case-studies/upload-pdf`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, pdf_url: res.data.pdf_url, pdf_filename: res.data.filename }));
      toast.success('PDF uploaded');
    } catch { toast.error('PDF upload failed'); }
    setUploadingPdf(false); e.target.value = '';
  };

  const removePdf = () => setForm(prev => ({ ...prev, pdf_url: '', pdf_filename: '' }));

  const handleSave = async () => {
    if (!form.title || !form.excerpt) { toast.error('Title and excerpt required'); return; }
    const payload = { ...form, slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60), date: form.date || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) };
    try {
      if (editStory) { await axios.put(`${API}/admin/case-studies/${editStory.id}`, payload, { headers: getAuthHeader() }); toast.success('Updated'); }
      else { await axios.post(`${API}/admin/case-studies`, payload, { headers: getAuthHeader() }); toast.success('Created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (story) => { setEditStory(story); setForm({ title: story.title || '', excerpt: story.excerpt || '', content: story.content || '', date: story.date || '', author: story.author || '', client: story.client || '', sector: story.sector || '', image_url: story.image_url || '', slug: story.slug || '', featured: !!story.featured }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/case-studies/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };
  const filtered = stories.filter(s => `${s.title} ${s.excerpt} ${s.client || ''} ${s.sector || ''}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="case-studies-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Case Studies</h1><p className="text-slate-500 text-sm">{stories.length} case studies</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-case-study-btn"><Plus size={15} className="mr-1" />Add Case Study</Button>
      </div>
      <div className="mb-4"><Input placeholder="Search case studies..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-72 border-slate-200" /></div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="case-study-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editStory ? 'Edit Case Study' : 'New Case Study'}</h3>
          <div className="space-y-4">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Case study headline" data-testid="case-study-title-input" /></div>
            <div><Label className="text-xs">Excerpt / Summary *</Label><Textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} placeholder="Brief description of the project, client, and outcome..." rows={2} data-testid="case-study-excerpt-input" /></div>
            <div><Label className="text-xs">Full Content</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write the full case study here..." rows={8} className="font-mono text-sm" data-testid="case-study-content-input" /><p className="text-[11px] text-slate-400 mt-1">Separate paragraphs with blank lines.</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">Client</Label><Input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} placeholder="e.g. North Somerset Council" data-testid="case-study-client-input" /></div>
              <div><Label className="text-xs">Sector / Industry</Label><Input value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })} placeholder="e.g. Local Government" data-testid="case-study-sector-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">Date</Label><Input value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} placeholder="e.g. 23/02/2026" /></div>
              <div><Label className="text-xs">Author</Label><Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} placeholder="Author / Team" /></div>
            </div>
            <div><Label className="text-xs">Image</Label>
              <div className="flex gap-3 items-start"><div className="flex-1"><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="Paste URL or upload..." /></div>
                <label className="cursor-pointer shrink-0"><Button variant="outline" size="sm" asChild disabled={uploading}><span><UploadSimple size={15} className="mr-1" />{uploading ? 'Uploading...' : 'Upload Image'}</span></Button><input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} /></label>
              </div>
              {form.image_url && <div className="mt-2 w-40 h-24 rounded-lg overflow-hidden border border-slate-200"><img src={form.image_url} alt="Preview" className="w-full h-full object-cover" /></div>}
            </div>
            <div><Label className="text-xs">URL Slug (auto-generated if blank)</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="e.g. revo-wholesale-partner-switch" /></div>
            <div className="pt-3 border-t border-slate-100">
              <Label className="text-xs flex items-center gap-1.5 mb-2"><FilePdf size={14} className="text-lime-600" weight="duotone" />PDF Attachment (optional)</Label>
              {form.pdf_url ? (
                <div className="flex items-center justify-between gap-3 p-3 bg-lime-50 border border-lime-200 rounded-lg" data-testid="case-study-pdf-attached">
                  <div className="flex items-center gap-2 min-w-0">
                    <FilePdf size={20} weight="duotone" className="text-lime-700 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{form.pdf_filename || 'Attached PDF'}</p>
                      <a
                        href={form.pdf_url.startsWith('http') ? form.pdf_url : `${BACKEND_URL}${form.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-lime-700 hover:underline"
                      >
                        Preview in new tab
                      </a>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={removePdf}
                    className="text-red-500 border-red-200 hover:bg-red-50 shrink-0"
                    data-testid="case-study-remove-pdf"
                  >
                    <X size={14} className="mr-1" />Remove
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-20 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-lime-400 hover:bg-lime-50/40 transition-colors">
                  <div className="text-center">
                    <UploadSimple size={20} className="mx-auto text-slate-400 mb-1" />
                    <p className="text-xs text-slate-500">{uploadingPdf ? 'Uploading…' : 'Click to upload a PDF'}</p>
                  </div>
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handlePdfUpload} data-testid="case-study-pdf-upload" />
                </label>
              )}
              <p className="text-[11px] text-slate-400 mt-1.5">When attached, visitors can hover-preview on desktop and tap a &quot;View PDF&quot; button on mobile, with a download link on the article page.</p>
            </div>
            <div className="pt-2 border-t border-slate-100">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="case-study-featured-toggle-label">
                <div className="relative shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={e => setForm({ ...form, featured: e.target.checked })}
                    className="sr-only peer"
                    data-testid="case-study-featured-toggle"
                  />
                  <div className="w-10 h-5 bg-slate-200 peer-checked:bg-lime-600 rounded-full transition-colors duration-200" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    <Star size={14} weight={form.featured ? 'fill' : 'regular'} className={form.featured ? 'text-lime-600' : 'text-slate-400'} />
                    Featured case study
                  </div>
                  <p className="text-[11px] text-slate-500">Pinned to the top of the public list and the Home page strip, regardless of date.</p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-case-study-btn"><FloppyDisk size={15} className="mr-1" />{editStory ? 'Update' : 'Publish'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="divide-y divide-slate-100">
          {filtered.map(story => (
            <div key={story.id} className="p-4 flex items-center gap-4" data-testid={`case-study-${story.id}`}>
              {story.image_url && <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0 border border-slate-200"><img src={story.image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900 truncate">{story.title}</p>
                  {story.featured && <span className="text-[10px] px-1.5 py-0.5 bg-lime-600 text-white rounded inline-flex items-center gap-1 shrink-0"><Star size={10} weight="fill" />Featured</span>}
                  {story.pdf_url && <span className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-white rounded inline-flex items-center gap-1 shrink-0"><FilePdf size={10} weight="fill" />PDF</span>}
                  {story.content && <span className="text-[10px] px-1.5 py-0.5 bg-lime-100 text-lime-700 rounded shrink-0">Full content</span>}
                </div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  {story.date && <span className="text-xs text-slate-500">{story.date}</span>}
                  {story.client && <><span className="text-xs text-slate-300">|</span><span className="text-xs text-slate-500">{story.client}</span></>}
                  {story.sector && <><span className="text-xs text-slate-300">|</span><span className="text-xs text-slate-500">{story.sector}</span></>}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{story.excerpt}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <ShareLinkedInButton type="case-studies" slug={story.slug} label="LinkedIn" />
                <Button variant="outline" size="sm" onClick={() => startEdit(story)}><PencilSimple size={14} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(story.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-12 text-center text-slate-500">{search ? 'No matches' : 'No case studies yet.'}</div>}
        </div>
      </div>
    </div>
  );
}
